import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { bookingsService, availabilityService } from '../../../services/api';

/**
 * ReallocateForm - Form for reallocating rooms or beds to different resources
 */
function ReallocateForm({ booking, entityType, selectedItem, onSuccess, onCancel }) {
  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [availableOptions, setAvailableOptions] = useState([]);
  
  // Form state
  const [selectedNewItem, setSelectedNewItem] = useState(null);
  const [reason, setReason] = useState('');
  const [adjustPricing, setAdjustPricing] = useState(true);
  const [priceDifference, setPriceDifference] = useState(0);

  // Load available options for reallocation
  useEffect(() => {
    if (selectedItem) {
      loadAvailableOptions();
    }
  }, [selectedItem]);

  const loadAvailableOptions = async () => {
    if (!selectedItem) return;

    setLoadingOptions(true);
    try {
      const params = {
        checkIn: selectedItem.checkIn,
        checkOut: selectedItem.checkOut,
        excludeBookingId: booking.id
      };

      let response;
      if (entityType === 'room') {
        response = await availabilityService.getRooms(params);
        console.log('Available rooms response for reallocation:', response);
        // Handle nested data structure and filter out the current room
        const rooms = response.data?.availableRooms || response.availableRooms || [];
        const filtered = rooms.filter(r => r.id !== selectedItem.roomId);
        console.log('Filtered rooms for reallocation:', filtered);
        setAvailableOptions(filtered);
      } else if (entityType === 'bed') {
        response = await availabilityService.getBeds(params);
        console.log('Available beds response for reallocation:', response);
        // Handle nested data structure and filter out the current bed
        const beds = response.data?.availableBeds || response.availableBeds || [];
        const filtered = beds.filter(b => b.id !== selectedItem.bedId);
        console.log('Filtered beds for reallocation:', filtered);
        setAvailableOptions(filtered);
      }
    } catch (error) {
      console.error('Error loading available options:', error);
      toast.error(`Failed to load available ${entityType}s`);
      setAvailableOptions([]);
    } finally {
      setLoadingOptions(false);
    }
  };

  // Calculate price difference when new item is selected
  useEffect(() => {
    if (selectedNewItem && adjustPricing) {
      calculatePriceDifference();
    } else {
      setPriceDifference(0);
    }
  }, [selectedNewItem, adjustPricing]);

  const calculatePriceDifference = () => {
    if (!selectedNewItem || !selectedItem) return;

    let currentPrice = 0;
    let newPrice = 0;

    if (entityType === 'room') {
      currentPrice = selectedItem.basePrice || 0;
      // Try multiple price sources: roomType.price, rate, or price field
      newPrice = selectedNewItem.roomType?.price || selectedNewItem.rate || selectedNewItem.price || 0;
    } else if (entityType === 'bed') {
      currentPrice = selectedItem.basePrice || 0;
      newPrice = selectedNewItem.price || selectedNewItem.roomType?.price || selectedNewItem.rate || 0;
    }

    // Calculate per-night difference and multiply by number of nights
    const nights = selectedItem.nights || 1;
    const difference = (newPrice - currentPrice) * nights;
    setPriceDifference(difference);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedNewItem) {
      toast.error(`Please select a new ${entityType}`);
      return;
    }

    if (!reason.trim()) {
      toast.error('Please provide a reason for reallocation');
      return;
    }

    // Confirmation for reallocation
    const confirmMessage = adjustPricing && priceDifference !== 0
      ? `Are you sure you want to reallocate this ${entityType}? The price will change by ₹${Math.abs(priceDifference).toFixed(2)} (${priceDifference > 0 ? 'increase' : 'decrease'}).`
      : `Are you sure you want to reallocate this ${entityType}?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setLoading(true);
    try {
      const data = {
        reason: reason.trim(),
        adjustPricing: adjustPricing
      };

      let response;
      if (entityType === 'room') {
        data.newRoomId = selectedNewItem.id;
        response = await bookingsService.reallocateRoom(booking.id, selectedItem.id, data);
        toast.success(`Room reallocated successfully to Room ${selectedNewItem.roomNumber}`);
      } else if (entityType === 'bed') {
        data.newBedId = selectedNewItem.id;
        response = await bookingsService.reallocateBed(booking.id, selectedItem.id, data);
        toast.success(`Bed reallocated successfully to Bed ${selectedNewItem.bedNumber}`);
      }

      if (onSuccess) {
        onSuccess(response);
      }
    } catch (error) {
      console.error('Error reallocating:', error);
      toast.error(error.error || error.message || 'Failed to reallocate');
    } finally {
      setLoading(false);
    }
  };

  if (!selectedItem) {
    return (
      <div style={{ backgroundColor: '#F5F3EB', padding: '20px', borderRadius: '8px', border: '1px solid #E3DFD3', textAlign: 'center' }}>
        <p style={{ color: '#5F6B61', fontSize: '14px' }}>Select a {entityType} to reallocate</p>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#F5F3EB', padding: '20px', borderRadius: '8px', border: '1px solid #E3DFD3' }}>
      <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#36433B' }}>
        Reallocate {entityType.charAt(0).toUpperCase() + entityType.slice(1)}
      </h4>

      {/* Current Item Info */}
      <div style={{ backgroundColor: '#F3E6CB', padding: '12px', borderRadius: '6px', marginBottom: '16px', border: '1px solid #B68235' }}>
        <div style={{ fontSize: '14px', fontWeight: '600', color: '#684A1F', marginBottom: '4px' }}>
          Current {entityType.charAt(0).toUpperCase() + entityType.slice(1)}:
        </div>
        <div style={{ fontSize: '13px', color: '#4F3818' }}>
          {entityType === 'room' && `Room ${selectedItem.roomNumber || selectedItem.room?.roomNumber} - ${selectedItem.roomTypeName || selectedItem.roomType?.name}`}
          {entityType === 'bed' && `Bed ${selectedItem.bedNumber || selectedItem.bed?.bedNumber} in Room ${selectedItem.roomNumber || selectedItem.room?.roomNumber}`}
          <div style={{ marginTop: '4px' }}>
            <strong>Check-in:</strong> {new Date(selectedItem.checkIn).toLocaleDateString()} | 
            <strong> Check-out:</strong> {new Date(selectedItem.checkOut).toLocaleDateString()} | 
            <strong> Nights:</strong> {selectedItem.nights || 0}
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Loading State */}
        {loadingOptions && (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <div style={{ fontSize: '14px', color: '#5F6B61' }}>Loading available {entityType}s...</div>
          </div>
        )}

        {/* Available Options */}
        {!loadingOptions && availableOptions.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#36433B', marginBottom: '8px' }}>
              Select New {entityType.charAt(0).toUpperCase() + entityType.slice(1)} *
            </label>
            <div style={{ maxHeight: '250px', overflowY: 'auto', border: '1px solid #D2CDBF', borderRadius: '6px', padding: '8px' }}>
              {availableOptions.map((option) => (
                <div
                  key={option.id}
                  onClick={() => setSelectedNewItem(option)}
                  style={{
                    padding: '12px',
                    marginBottom: '8px',
                    backgroundColor: selectedNewItem?.id === option.id ? '#E1E9E2' : 'white',
                    border: selectedNewItem?.id === option.id ? '2px solid #1A3E2A' : '1px solid #E3DFD3',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#1A3E2A' }}>
                    {entityType === 'room' && `Room ${option.roomNumber} - ${option.roomType?.name}`}
                    {entityType === 'bed' && `Bed ${option.bedNumber} in Room ${option.room?.roomNumber} - ${option.roomType?.name}`}
                  </div>
                  <div style={{ fontSize: '13px', color: '#5F6B61', marginTop: '4px' }}>
                    {entityType === 'room' && `₹${option.roomType?.price || option.rate || option.price || 0}/night • ${option.roomType?.isShared ? 'Shared' : 'Private'}`}
                    {entityType === 'bed' && `₹${option.price || option.roomType?.price || option.rate || 0}/night • ${option.isFemaleOnly ? 'Female Only' : 'Mixed'}`}
                  </div>
                  {option.property && (
                    <div style={{ fontSize: '12px', color: '#A5A89B', marginTop: '2px' }}>
                      {option.property.name || option.room?.property?.name}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* No Options Available */}
        {!loadingOptions && availableOptions.length === 0 && (
          <div style={{ backgroundColor: '#F9EEEB', padding: '16px', borderRadius: '6px', marginBottom: '16px', border: '1px solid #E5B3A9' }}>
            <div style={{ fontSize: '14px', color: '#5F241B', textAlign: 'center' }}>
              No available {entityType}s found for the selected dates. All {entityType}s are either booked or unavailable.
            </div>
            <div style={{ fontSize: '13px', color: '#481B14', textAlign: 'center', marginTop: '8px' }}>
              Please try extending the search or contact support to release blocked {entityType}s.
            </div>
          </div>
        )}

        {/* Pricing Adjustment Option */}
        {selectedNewItem && (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={adjustPricing}
                onChange={(e) => setAdjustPricing(e.target.checked)}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#36433B' }}>
                Adjust pricing based on new {entityType} rate
              </span>
            </label>

            {adjustPricing && priceDifference !== 0 && (
              <div style={{ 
                marginTop: '8px',
                padding: '10px',
                backgroundColor: priceDifference > 0 ? '#F3E6CB' : '#E1E9E2',
                borderRadius: '6px',
                border: `1px solid ${priceDifference > 0 ? '#C9903C' : '#1A3E2A'}`
              }}>
                <div style={{ fontSize: '13px', color: priceDifference > 0 ? '#684A1F' : '#0F2619' }}>
                  <strong>Price Change:</strong> {priceDifference > 0 ? '+' : ''}₹{priceDifference.toFixed(2)}
                  <div style={{ fontSize: '12px', marginTop: '4px', fontStyle: 'italic' }}>
                    Original: ₹{(selectedItem.basePrice * selectedItem.nights).toFixed(2)} → 
                    New: ₹{((selectedItem.basePrice * selectedItem.nights) + priceDifference).toFixed(2)}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Reason */}
        {selectedNewItem && (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#36433B', marginBottom: '4px' }}>
              Reason for Reallocation * <span style={{ fontSize: '12px', fontWeight: '400', color: '#5F6B61' }}>(required for audit)</span>
            </label>
            <select
              value={reason}
              onChange={(e) => {
                if (e.target.value === 'custom') {
                  setReason('');
                } else {
                  setReason(e.target.value);
                }
              }}
              required
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #D2CDBF',
                borderRadius: '6px',
                fontSize: '14px',
                marginBottom: '8px'
              }}
            >
              <option value="">Select a reason...</option>
              <option value="Original room under maintenance">Original Room Under Maintenance</option>
              <option value="Guest upgrade request">Guest Upgrade Request</option>
              <option value="Room type change">Room Type Change</option>
              <option value="Overbooking resolution">Overbooking Resolution</option>
              <option value="Guest preference (quieter location)">Guest Preference (Quieter Location)</option>
              <option value="Accessibility requirements">Accessibility Requirements</option>
              <option value="Better view/amenities">Better View/Amenities</option>
              <option value="custom">Custom Reason (type below)</option>
            </select>

            {!reason && (
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Enter custom reason..."
                required
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #D2CDBF',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            )}
          </div>
        )}

        {/* Reallocation Summary */}
        {selectedNewItem && reason && (
          <div style={{ 
            backgroundColor: '#E1E9E2', 
            padding: '12px', 
            borderRadius: '6px', 
            marginBottom: '16px',
            border: '1px solid #5F8A6E'
          }}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#0F2619', marginBottom: '8px' }}>
              Reallocation Summary:
            </div>
            <div style={{ fontSize: '13px', color: '#122E20', lineHeight: '1.6' }}>
              <div><strong>From:</strong> {entityType === 'room' ? `Room ${selectedItem.roomNumber}` : `Bed ${selectedItem.bedNumber}`}</div>
              <div><strong>To:</strong> {entityType === 'room' ? `Room ${selectedNewItem.roomNumber}` : `Bed ${selectedNewItem.bedNumber}`}</div>
              <div><strong>Reason:</strong> {reason}</div>
              {adjustPricing && priceDifference !== 0 && (
                <div><strong>Price Adjustment:</strong> {priceDifference > 0 ? '+' : ''}₹{priceDifference.toFixed(2)}</div>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            style={{
              backgroundColor: 'white',
              color: '#36433B',
              border: '1px solid #D2CDBF',
              padding: '8px 16px',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || !selectedNewItem || !reason || availableOptions.length === 0}
            style={{
              backgroundColor: loading || !selectedNewItem || !reason || availableOptions.length === 0 ? '#A5A89B' : '#A9613C',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: loading || !selectedNewItem || !reason || availableOptions.length === 0 ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? 'Reallocating...' : 'Reallocate ' + entityType.charAt(0).toUpperCase() + entityType.slice(1)}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ReallocateForm;
